import { GalleryImage } from '@phoenix/core/frontend/shared/ui/images/images-gallery/images-gallery';
import {
  GuideBar,
  ImageVariants,
  SawChain,
  BatteryDetails,
  ChargerDetails,
  ProductRegistrationQuestions,
} from '@phoenix/team-m/my-tool-shed/frontend/domain/generated/graphql';

export interface Brand {
  id: string;
  name: string;
}

export interface CategoryGroup {
  id?: string;
  name: string;
}

export interface ProductCategory {
  id?: string;
  name: string;
  categoryGroup?: CategoryGroup;
}

export interface ProductDriveType {
  id?: string;
  name: string;
}

export interface ProductImages {
  main?: string;
  system?: string;
}

enum ServiceReminderInterval {
  CustomDate = 'CUSTOM_DATE',
  NotRemindMe = 'NOT_REMIND_ME',
  OneMonthBefore = 'ONE_MONTH_BEFORE',
  OneWeekBefore = 'ONE_WEEK_BEFORE',
  SameDay = 'SAME_DAY',
}

export interface ServiceReminder {
  interval: ServiceReminderInterval;
  remindByEmail: boolean;
  nextServiceDate?: string;
  reminderDate?: string;
}

export interface ProductListItem {
  id: string;
  serialNumber?: string;
  materialNumber?: string;
  articleNumber?: string;
  productCategory?: ProductCategory;
  productDriveType?: ProductDriveType;
  brand?: Brand;
  customName?: string;
  productName: string;
  purchaseDate?: string;
  registrationDate?: string;
  warrantyEndDate?: string;
  images: ProductImages;
  isIMow: boolean;
  isVerifiedIMow: boolean;
  serviceReminder?: ServiceReminder;
  warrantyExtensionDate?: string;
  isWarrantyExtensionPossible?: boolean;
  questions?: ProductRegistrationQuestions;
}

export interface ProductWithSerialNumber extends Omit<ProductListItem, 'serialNumber'> {
  serialNumber: string;
}

export interface ProductServiceProtocol {
  id: number;
  creationDate: string;
  name: string;
  file: string;
}

export interface ProductAttachment {
  id: string;
  url: string;
  mimeType: string;
  originalFilename: string;
  createdAt: string;
  imageVariants?: ImageVariants;
  defaultProfileImage?: boolean;
}

export interface ProductDetails extends ProductListItem {
  isPetrol: boolean;
  serviceProtocols: ProductServiceProtocol[];
  galleryImages?: GalleryImage[];
  documents?: ProductAttachment[];
  userManualUrl?: string;
}

export interface ProductAccessory {
  id: string;
  favoriteId?: string;
  isObsolete: boolean;
  isFavorite: boolean;
  isSaving?: boolean;
}

export interface ProductChainAndBarCombination extends ProductAccessory {
  materialNumber: string;
  chain: SawChain;
  bar: GuideBar;
}

export interface ProductCuttingAttachment extends ProductAccessory {
  productName: string;
  productTitle: string;
  materialNumber: string;
  imageUrl: string;
  articleNumber: string;
  category: string;
  metalName?: string;
  accessoryUrl?: string;
}

export interface BatteryRuntime {
  powerLevel?: number;
  runtime?: number;
}

export interface ProductBattery extends ProductAccessory {
  productName: string;
  materialNumber: string;
  articleNumber?: string;
  imageUrl?: string;
  accessoryUrl?: string;
  runtimes: BatteryRuntime[];
  compatibleChargers?: ChargerDetails[];
}

export interface ProductCharger extends ProductAccessory {
  productName: string;
  materialNumber: string;
  articleNumber?: string;
  imageUrl?: string;
  accessoryUrl?: string;
  compatibleBatteries?: BatteryDetails[];
}

export interface ProductSparePart extends ProductAccessory {
  name: string;
  imageUrl?: string;
  accessoryUrl?: string;
}

export interface ServiceKitComponent {
  materialNumber: string;
  text: string;
}

export interface ProductServiceKit extends ProductAccessory {
  materialNumber: string;
  name: string;
  imageUrl?: string;
  accessoryUrl?: string;
  components?: ServiceKitComponent[];
}

export enum ProductAccessoriesType {
  ChainAndBarCombination = 'chainAndBarCombination',
  CuttingAttachment = 'cuttingAttachment',
  Battery = 'battery',
  Charger = 'charger',
  SparePart = 'sparePart',
  ServiceKit = 'serviceKit',
}

export type ProductAccessories = {
  [ProductAccessoriesType.ChainAndBarCombination]: ProductChainAndBarCombination[];
  [ProductAccessoriesType.CuttingAttachment]: ProductCuttingAttachment[];
  [ProductAccessoriesType.Battery]: ProductBattery[];
  [ProductAccessoriesType.Charger]: ProductCharger[];
  [ProductAccessoriesType.SparePart]: ProductSparePart[];
  [ProductAccessoriesType.ServiceKit]: ProductServiceKit[];
};
